import { useTranslation } from '@Providers/TranslationProvider';
import { useDesignSchema } from '@Providers/DesignSchemaProvider';
import { Link } from '@Components/RoutesUtils';
import { useConfig } from '@Src/providers/ConfigProvider';
import { buildSocialMediaIconList } from '@Components/LegacyElixir/SocialMedia/SocialMedia';
import MobileAppBadges from '@Components/Widgets/Apps/MobileAppBadges';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { StyledLogoLink } from '../Header';
import { ReactComponent as FlipdishPoweredByIcon } from '@Src/assets/images/flipdish_powered_by.svg';
import Typography from '@mui/material/Typography';
import LanguageSelector from './LanguageSelector';

const StyledSublinkContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(0.5)};
  margin-right: ${({ theme }) => theme.spacing(4)};
`;

const StyledSublink = styled(Link)`
  text-decoration: none !important;

  &:hover,
  &:focus {
    opacity: 0.5;
  }
`;

const StyledSublinkanchor = styled('a')`
  text-decoration: none !important;
  cursor: pointer;
  &:hover,
  &:focus {
    opacity: 0.5;
  }
`;

const StyledFooter = styled('footer')`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-left: 5rem !important;
    padding-right: 5rem !important;
    padding-top: ${({ theme }) => theme.spacing(7.5)};
    padding-bottom: ${({ theme }) => theme.spacing(7.5)};
  }

  background: ${({ theme }) => theme.palette.secondary.main};

  color: ${(props) => props.theme.palette.secondary.contrastText};

  p {
    color: ${(props) => props.theme.palette.secondary.contrastText};
  }
  position: relative;
  padding-top: ${({ theme }) => theme.spacing(4)};
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

const StyledMobileBadgeAndSocialMediaContainer = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up('md')} {
    justify-content: flex-end;
    margin-right: ${({ theme }) => theme.spacing(6)};
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: ${({ theme }) => theme.spacing(2.5)};
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledSocialMediaList = styled('ul')`
  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  gap: ${({ theme }) => theme.spacing(1.5)};
  a {
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 48px;
    height: 48px;
    background: ${({ theme }) => theme.palette.background.default};
    border-radius: 100px !important;
    &:hover {
      svg {
        path {
          fill: ${({ theme }) =>
            theme.palette.getContrastText(theme.palette.background.default)};
        }
      }
    }
  }
  svg {
    width: 24px;
    height: 24px;
    path {
      fill: ${({ theme }) =>
        theme.palette.getContrastText(theme.palette.background.default)};
    }
  }
`;

const StyledTopLineFooter = styled(Grid)`
  padding-left: ${({ theme }) => theme.spacing(3)};
  padding-right: ${({ theme }) => theme.spacing(4)};
`;

const StyledPoweredByIcon = styled(FlipdishPoweredByIcon)`
  margin-right: ${({ theme }) => theme.spacing(1)};
  path {
    fill: ${({ theme }) => theme.palette.secondary.contrastText};
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const SyledLogoSublinkGridItem = styled(Grid)`
  align-items: center;
  width: 50%;
  display: flex;
  justify-content: start;
  gap: ${({ theme }) => theme.spacing(2.75)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-direction: column;
    justify-content: left;
    align-items: start;
    width: 100%;
  }
`;

const StyledBadgesContainer = styled('div')`
  display: flex;
  flex-direction: row;
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    margin-right: ${({ theme }) => theme.spacing(2)}!important;
  }
`;

const StyledLogo = styled('img')`
  height: 64px;
  border-radius: 20%;
`;

const StyledMobileAndSocialMediaItem = styled(Grid)`
  width: 50%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const StyledBottomLineFooter = styled(Grid)`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing(3.25)};
`;

const StyledFlipdishContainer = styled(Grid)`
  display: flex;
`;

const FlipdishIconTextContainer = styled(Grid)`
  align-items: end;
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing(0.25)};
  color: ${({ theme }) => theme.palette.secondary.contrastText};
`;

const Footer = (): JSX.Element => {
  const { socialMedia, config, imageAttributes, name } = useDesignSchema('v2');
  const { policyUrls, logo } = config || {};
  const { translate } = useTranslation();
  const { language, supportedLanguages } = useConfig();
  const showLanguageSelector = supportedLanguages.length > 1;
  const logoAlt = logo
    ? imageAttributes?.[logo]?.alt || 'Our_logo'
    : 'Our_logo';

  const handleCookieSettingsClick = (): void => {
    window.fdCc?.edit({
      lang: language,
      restaurantName: name,
      logoUrl: logo,
      cookiePolicyUrl: policyUrls?.cookie || 'cookiepolicy',
    });
  };

  return (
    <StyledFooter id="footer" className="section" role="contentinfo">
      <StyledTopLineFooter container>
        <SyledLogoSublinkGridItem item>
          <StyledLogoLink data-fd="logo" to="/">
            <StyledLogo alt={translate(logoAlt)} src={logo} />
          </StyledLogoLink>

          <StyledSublinkContainer className="footerlink">
            <StyledSublink
              to={policyUrls?.terms ?? '/terms'}
              target="_blank"
              rel="noreferrer"
            >
              <Typography variant="body2">{translate('Terms')}</Typography>
            </StyledSublink>{' '}
            |{' '}
            {policyUrls?.privacy ? (
              <>
                <StyledSublink
                  to={policyUrls.privacy}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Typography variant="body2">
                    {' '}
                    {translate('Privacy')}
                  </Typography>
                </StyledSublink>
              </>
            ) : (
              <StyledSublink to="/privacy" target="_blank" rel="noreferrer">
                <Typography variant="body2"> {translate('Privacy')}</Typography>
              </StyledSublink>
            )}{' '}
            |{' '}
            {policyUrls?.cookie ? (
              <StyledSublink
                to={policyUrls.cookie}
                target="_blank"
                rel="noreferrer"
              >
                <Typography variant="body2">
                  {' '}
                  {translate('Cookie_Policy')}
                </Typography>
              </StyledSublink>
            ) : (
              <StyledSublink
                to={'/cookiepolicy'}
                target="_blank"
                rel="noreferrer"
              >
                <Typography variant="body2">
                  {translate('Cookie_Policy')}
                </Typography>
              </StyledSublink>
            )}{' '}
            |{' '}
            <StyledSublinkanchor onClick={handleCookieSettingsClick}>
              <Typography variant="body2">
                {translate('Cookie_Settings')}
              </Typography>
            </StyledSublinkanchor>
          </StyledSublinkContainer>
        </SyledLogoSublinkGridItem>
        <StyledMobileAndSocialMediaItem item>
          <StyledMobileBadgeAndSocialMediaContainer>
            <StyledBadgesContainer>
              <MobileAppBadges />
            </StyledBadgesContainer>
            <StyledSocialMediaList>
              {buildSocialMediaIconList(socialMedia, true)}
            </StyledSocialMediaList>
          </StyledMobileBadgeAndSocialMediaContainer>
        </StyledMobileAndSocialMediaItem>
        <StyledBottomLineFooter item xs={12}>
          <StyledFlipdishContainer>
            <StyledLink
              to="https://www.flipdish.com/"
              target="_blank"
              aria-label={translate('Online_ordering_powered_by_Flipdish')}
              rel="noreferrer"
            >
              <Grid>
                <FlipdishIconTextContainer>
                  <StyledPoweredByIcon />
                  <Typography
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                    variant="body2Medium"
                  >
                    {translate('Online_ordering_powered_by_Flipdish')}
                  </Typography>
                </FlipdishIconTextContainer>
                <Typography
                  sx={{ display: { xs: 'flex', sm: 'none' } }}
                  variant="body2Medium"
                >
                  {translate('Online_ordering_powered_by_Flipdish')}
                </Typography>
                <Typography display="none" variant="finePrint3">
                  {/* Copyright text goes here */}
                </Typography>
              </Grid>
            </StyledLink>
          </StyledFlipdishContainer>
          {showLanguageSelector && (
            <Grid>
              <LanguageSelector />
            </Grid>
          )}
        </StyledBottomLineFooter>
      </StyledTopLineFooter>
    </StyledFooter>
  );
};

export default Footer;
